import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { toast } from 'sonner';
import { useAuth } from '@/store/useAuth';
import { getNotificationPreferences, updateNotificationPreferences } from '@/services/authClient';
import { PANEL_URL } from '@/config/brand';

const PANEL_PROFILE_URL = `${new URL(PANEL_URL).origin}/panel/profile/`;

export default function Profile() {
  const { user, updateProfile } = useAuth();
  const [info, setInfo] = useState({ name: user?.name || '', email: user?.email || '', phone: user?.phone || '' });
  const [prefs, setPrefs] = useState(null);

  useEffect(() => {
    getNotificationPreferences()
      .then((data) => setPrefs({
        emailEnabled: data.email_enabled ?? true,
        inAppEnabled: data.in_app_enabled ?? true,
        onNewListing: data.on_new_listing ?? true,
        onPriceDrop: data.on_price_drop ?? true,
        onStatusChange: data.on_status_change ?? false,
      }))
      .catch(() => setPrefs({
        emailEnabled: true,
        inAppEnabled: true,
        onNewListing: true,
        onPriceDrop: true,
        onStatusChange: false,
      }));
  }, []);

  const saveInfo = async (e) => {
    e.preventDefault();
    try {
      await updateProfile({ first_name: info.name.split(' ')[0], last_name: info.name.split(' ').slice(1).join(' '), phone: info.phone });
      toast('Profile updated');
    } catch {
      toast.error('Could not update profile. Please try again.');
    }
  };

  const updatePref = async (key, value) => {
    const apiKey = key.replace(/([A-Z])/g, '_$1').toLowerCase();
    try {
      await updateNotificationPreferences({ [apiKey]: value });
      setPrefs({ ...prefs, [key]: value });
      toast('Preferences updated');
    } catch {
      toast.error('Could not update preferences');
    }
  };

  return (
    <div className="space-y-6">
      <h1 className="font-display text-2xl font-semibold text-slate-900">Profile settings</h1>

      {/* Avatar + info */}
      <form onSubmit={saveInfo} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex items-center gap-4">
          <div>
            <Avatar className="h-20 w-20"><AvatarImage src={user?.avatar} alt={user?.name} /><AvatarFallback>{user?.name?.[0]}</AvatarFallback></Avatar>
          </div>
          <div><p className="font-semibold text-slate-900">{user?.name}</p><p className="text-sm text-slate-500">Member since {new Date(user?.joined || Date.now()).getFullYear()}</p></div>
        </div>
        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          <div><Label>Full name</Label><Input value={info.name} onChange={(e) => setInfo({ ...info, name: e.target.value })} className="mt-1.5 rounded-xl" data-testid="profile-name" /></div>
          <div>
            <Label>Email</Label>
            <Input type="email" value={info.email} readOnly disabled className="mt-1.5 rounded-xl bg-slate-50 text-slate-600" data-testid="profile-email" />
            <p className="mt-1 text-xs text-slate-500">Email can&rsquo;t be changed here. Contact support to update it.</p>
          </div>
          <div><Label>Phone</Label><Input value={info.phone} onChange={(e) => setInfo({ ...info, phone: e.target.value })} placeholder="(555) 000-0000" className="mt-1.5 rounded-xl" data-testid="profile-phone" /></div>
        </div>
        <Button type="submit" className="mt-5 rounded-xl" data-testid="profile-save">Save changes</Button>
      </form>

      {/* Password — change-password API yok; panel veya forgot-password */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-slate-900">Change password</h2>
        <p className="mt-2 text-sm text-slate-600">
          Password changes are managed in your CasaHom panel account settings.
        </p>
        <Button asChild variant="outline" className="mt-5 rounded-xl" data-testid="password-save">
          <a href={PANEL_PROFILE_URL}>Change password in panel</a>
        </Button>
        <p className="mt-3 text-sm text-slate-500">
          Or{' '}
          <Link to="/forgot-password" className="font-medium text-blue-600 hover:underline">
            reset via email
          </Link>
        </p>
      </div>

      {/* Notifications */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-slate-900">Notification preferences</h2>
        {prefs === null ? (
          <div className="mt-4 text-sm text-slate-500">Loading...</div>
        ) : (
          <div className="mt-4 space-y-3">
            {[
              ['emailEnabled', 'Email notifications'],
              ['inAppEnabled', 'In-app notifications'],
              ['onNewListing', 'New listing matches'],
              ['onPriceDrop', 'Price drop alerts'],
              ['onStatusChange', 'Status change notifications'],
            ].map(([key, label]) => (
              <label key={key} className="flex items-center justify-between border-b border-slate-100 py-2 text-sm text-slate-700">
                {label}
                <Switch
                  checked={prefs[key]}
                  onCheckedChange={(v) => updatePref(key, v)}
                  data-testid={`pref-${key}`}
                />
              </label>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
